import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { analyticsAPI, propertiesAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, TrendingDown, Calendar, DollarSign, Target, 
  BarChart3, Activity, Loader2, Info, Zap, Sun, Moon,
  AlertTriangle, CheckCircle2, ArrowRight, Sparkles
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, AreaChart, Area, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DemandForecastPage = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('ihostpms_token');
  
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [forecastDays, setForecastDays] = useState(90);
  
  // Load properties
  useEffect(() => {
    const loadProperties = async () => {
      try {
        const resp = await propertiesAPI.list();
        const props = resp.data.properties || resp.data || [];
        setProperties(props);
        if (props.length > 0) {
          setSelectedProperty(props[0]);
        }
      } catch (err) {
        console.error('Failed to load properties:', err);
        // Don't redirect on error - let the user stay on page
      } finally {
        setLoading(false);
      }
    };
    
    // Only load if we have a token
    const token = localStorage.getItem('ihostpms_token');
    if (token) {
      loadProperties();
    } else {
      setLoading(false);
    }
  }, []);
  
  // Load KPIs
  useEffect(() => {
    const loadKpis = async () => {
      try {
        const resp = await analyticsAPI.kpis({
          property_id: selectedProperty?.property_id
        });
        setKpis(resp.data);
      } catch (err) {
        console.error('Failed to load KPIs:', err);
      }
    };
    if (selectedProperty) loadKpis();
  }, [selectedProperty]);
  
  // Load forecast when property changes
  const loadForecast = useCallback(async () => {
    if (!selectedProperty) return;
    
    setLoadingForecast(true);
    try {
      const resp = await analyticsAPI.forecast(selectedProperty.property_id, forecastDays);
      setForecast(resp.data);
    } catch (err) {
      console.error('Failed to load forecast:', err);
    } finally {
      setLoadingForecast(false);
    }
  }, [selectedProperty, forecastDays]);
  
  useEffect(() => {
    loadForecast();
  }, [loadForecast]);
  
  // Load heatmap
  useEffect(() => {
    const loadHeatmap = async () => {
      try {
        const resp = await analyticsAPI.heatmap({
          property_id: selectedProperty?.property_id
        });
        setHeatmap(resp.data);
      } catch (err) {
        console.error('Failed to load heatmap:', err);
      }
    };
    if (selectedProperty) loadHeatmap();
  }, [selectedProperty]);
  
  const formatCurrency = (value) => {
    const currency = selectedProperty?.currency || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value || 0);
  };
  
  const getDemandColor = (level) => {
    const colors = {
      very_high: 'bg-red-500',
      high: 'bg-orange-500',
      moderate: 'bg-yellow-500',
      low: 'bg-blue-400',
      very_low: 'bg-blue-200'
    };
    return colors[level] || 'bg-gray-300';
  };
  
  const getDemandLabel = (level) => {
    const labels = {
      very_high: 'Very High',
      high: 'High',
      moderate: 'Moderate',
      low: 'Low',
      very_low: 'Very Low'
    };
    return labels[level] || level;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-airbnb-pink" />
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="demand-forecast-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            Demand Forecasting
          </h1>
          <p className="text-gray-500 mt-1">AI-powered predictions to maximize your revenue</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select
            value={selectedProperty?.property_id || ''}
            onValueChange={(val) => setSelectedProperty(properties.find(p => p.property_id === val))}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select property" />
            </SelectTrigger>
            <SelectContent>
              {properties.map(p => (
                <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={String(forecastDays)} onValueChange={(val) => setForecastDays(parseInt(val))}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 Days</SelectItem>
              <SelectItem value="60">60 Days</SelectItem>
              <SelectItem value="90">90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Tip Banner */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-amber-900">How to Use This Data</h4>
            <p className="text-sm text-amber-700 mt-1">
              Use demand forecasts to adjust your pricing strategy. Raise prices during predicted high-demand 
              periods and offer discounts during slow periods to maximize both occupancy and revenue.
            </p>
          </div>
        </div>
      </div>
      
      <Tabs defaultValue="forecast" className="space-y-6">
        <TabsList>
          <TabsTrigger value="forecast">
            <TrendingUp className="w-4 h-4 mr-2" />
            Forecast
          </TabsTrigger>
          <TabsTrigger value="kpis">
            <Target className="w-4 h-4 mr-2" />
            KPIs
          </TabsTrigger>
          <TabsTrigger value="heatmap">
            <BarChart3 className="w-4 h-4 mr-2" />
            Patterns
          </TabsTrigger>
        </TabsList>
        
        {/* Forecast Tab */}
        <TabsContent value="forecast">
          {loadingForecast ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-airbnb-pink" />
            </div>
          ) : forecast ? (
            <div className="space-y-6">
              {/* Revenue Projections */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Next 30 Days</CardDescription>
                    <CardTitle className="text-2xl text-green-600">
                      {formatCurrency(forecast.revenue_forecast?.next_30_days?.expected)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500">
                      Range: {formatCurrency(forecast.revenue_forecast?.next_30_days?.low)} - {formatCurrency(forecast.revenue_forecast?.next_30_days?.high)}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      ~{forecast.revenue_forecast?.next_30_days?.nights_expected?.toFixed(0)} nights expected
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Next 60 Days</CardDescription>
                    <CardTitle className="text-2xl text-blue-600">
                      {formatCurrency(forecast.revenue_forecast?.next_60_days?.expected)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500">
                      Range: {formatCurrency(forecast.revenue_forecast?.next_60_days?.low)} - {formatCurrency(forecast.revenue_forecast?.next_60_days?.high)}
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>RevPAR (30 Day)</CardDescription>
                    <CardTitle className="text-2xl text-purple-600">
                      {formatCurrency(forecast.revenue_forecast?.projected_revpar_30)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500">Revenue Per Available Night</p>
                    <p className="text-sm text-gray-600 mt-1">
                      ADR: {formatCurrency(forecast.revenue_forecast?.avg_daily_rate)}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Booking Pace */}
              {forecast.booking_pace && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Booking Pace
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold">{forecast.booking_pace.bookings_per_week}</p>
                        <p className="text-sm text-gray-500">bookings/week</p>
                      </div>
                      <div className="text-right">
                        <Badge className={
                          forecast.booking_pace.pace_status === 'strong' ? 'bg-green-100 text-green-700' :
                          forecast.booking_pace.pace_status === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {forecast.booking_pace.pace_status}
                        </Badge>
                        <p className="text-sm text-gray-500 mt-1">{forecast.booking_pace.pace_message}</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-gray-600">Next 30 days fill rate</p>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-airbnb-pink h-2 rounded-full" 
                          style={{ width: `${Math.min(forecast.booking_pace.upcoming_30_day_fill, 100)}%` }} 
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{forecast.booking_pace.upcoming_30_day_fill}% booked</p>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Peak Periods */}
              {forecast.peak_periods?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      Predicted Peak Periods
                    </CardTitle>
                    <CardDescription>Consider raising prices during these dates</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {forecast.peak_periods.slice(0, 5).map((peak, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${peak.level === 'very_high' ? 'bg-red-500' : 'bg-orange-500'}`} />
                            <div>
                              <p className="font-medium">{peak.start_date} → {peak.end_date}</p>
                              <p className="text-sm text-gray-500">{peak.days} days</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="border-amber-300 text-amber-700">
                            {getDemandLabel(peak.level)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Daily Forecast Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Daily Demand Forecast</CardTitle>
                  <CardDescription>Predicted occupancy probability for the next 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={forecast.daily_forecasts}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis 
                          domain={[0, 1]} 
                          tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip 
                          formatter={(val) => [`${(val * 100).toFixed(0)}%`, 'Occupancy Prob.']}
                          labelFormatter={(val) => new Date(val).toLocaleDateString()}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="occupancy_probability" 
                          stroke="#FF385C" 
                          fill="#FF385C" 
                          fillOpacity={0.2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Monthly Projections */}
              {forecast.monthly_forecasts?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Monthly Revenue Projections</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={forecast.monthly_forecasts}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(val) => [formatCurrency(val), 'Expected Revenue']} />
                          <Bar dataKey="expected_revenue" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      {forecast.monthly_forecasts.map((month, idx) => (
                        <div key={idx} className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium">{month.month_name}</p>
                          <p className="text-lg font-bold text-indigo-600">{month.forecasted_occupancy}%</p>
                          <p className="text-xs text-gray-500">occupancy</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Confidence Note */}
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Forecast Confidence Note</p>
                    <p className="text-sm text-blue-700">
                      {forecast.confidence_note || 'Forecasts are based on statistical patterns and historical data. Actual results may vary based on market conditions.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-20 text-gray-500">
              Select a property to view demand forecast
            </div>
          )}
        </TabsContent>
        
        {/* KPIs Tab */}
        <TabsContent value="kpis">
          {kpis ? (
            <div className="space-y-6">
              {/* Main KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>RevPAR</CardDescription>
                    <CardTitle className="text-2xl text-green-600">
                      {formatCurrency(kpis.kpis?.revpar?.value)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500">{kpis.kpis?.revpar?.description}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>ADR</CardDescription>
                    <CardTitle className="text-2xl text-blue-600">
                      {formatCurrency(kpis.kpis?.adr?.value)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500">{kpis.kpis?.adr?.description}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Occupancy</CardDescription>
                    <CardTitle className="text-2xl text-purple-600">
                      {kpis.kpis?.occupancy_rate?.value}%
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                      <div 
                        className="bg-purple-500 h-2 rounded-full" 
                        style={{ width: `${Math.min(kpis.kpis?.occupancy_rate?.value || 0, 100)}%` }} 
                      />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Total Revenue</CardDescription>
                    <CardTitle className="text-2xl">
                      {formatCurrency(kpis.kpis?.total_revenue?.value)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-1">
                      {kpis.kpis?.total_revenue?.change_percent >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      )}
                      <span className={`text-xs ${kpis.kpis?.total_revenue?.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {kpis.kpis?.total_revenue?.change_percent?.toFixed(1)}% vs last period
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Secondary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Avg Lead Time</CardDescription>
                    <CardTitle>{kpis.kpis?.avg_lead_time?.value} days</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500">{kpis.kpis?.avg_lead_time?.description}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Avg Stay Length</CardDescription>
                    <CardTitle>{kpis.kpis?.avg_length_of_stay?.value} nights</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500">{kpis.kpis?.avg_length_of_stay?.description}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Cancellation Rate</CardDescription>
                    <CardTitle className={kpis.kpis?.cancellation_rate?.value > 10 ? 'text-red-600' : ''}>
                      {kpis.kpis?.cancellation_rate?.value}%
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-500">{kpis.kpis?.cancellation_rate?.description}</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Revenue by Channel */}
              {kpis.revenue_by_channel?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Revenue by Channel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {kpis.revenue_by_channel.map((channel, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${
                              channel.channel === 'direct' ? 'bg-green-500' :
                              channel.channel === 'iStayBnB' ? 'bg-pink-500' :
                              channel.channel === 'wordpress_widget' ? 'bg-blue-500' :
                              'bg-gray-400'
                            }`} />
                            <span className="font-medium capitalize">{channel.channel}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{formatCurrency(channel.revenue)}</p>
                            <p className="text-xs text-gray-500">{channel.percentage}% • {channel.bookings} bookings</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Period Summary</CardTitle>
                  <CardDescription>
                    {kpis.period?.start_date} to {kpis.period?.end_date} ({kpis.period?.days} days)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{kpis.summary?.total_bookings}</p>
                      <p className="text-sm text-gray-500">Bookings</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{kpis.summary?.total_nights_sold}</p>
                      <p className="text-sm text-gray-500">Nights Sold</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{kpis.summary?.available_nights}</p>
                      <p className="text-sm text-gray-500">Available</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{kpis.summary?.num_properties}</p>
                      <p className="text-sm text-gray-500">Properties</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-500">{kpis.summary?.cancelled_bookings}</p>
                      <p className="text-sm text-gray-500">Cancelled</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-20 text-gray-500">
              Loading KPIs...
            </div>
          )}
        </TabsContent>
        
        {/* Patterns/Heatmap Tab */}
        <TabsContent value="heatmap">
          {heatmap ? (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Occupancy Patterns</CardTitle>
                  <CardDescription>
                    See which months and days of the week have the most bookings ({heatmap.year})
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <div className="grid gap-1" style={{ gridTemplateColumns: `100px repeat(12, 1fr)` }}>
                      {/* Header - Months */}
                      <div></div>
                      {heatmap.months?.map((month, idx) => (
                        <div key={idx} className="text-xs text-center font-medium text-gray-500">
                          {month}
                        </div>
                      ))}
                      
                      {/* Rows - Days of week */}
                      {heatmap.days?.map((day, dayIdx) => (
                        <React.Fragment key={dayIdx}>
                          <div className="text-xs font-medium text-gray-500 pr-2 text-right">
                            {day}
                          </div>
                          {heatmap.months?.map((month, monthIdx) => {
                            const cell = heatmap.heatmap_data?.find(
                              d => d.dow_idx === dayIdx && d.month_idx === (monthIdx + 1)
                            );
                            const value = cell?.value || 0;
                            const maxValue = Math.max(...(heatmap.heatmap_data?.map(d => d.value) || [1]));
                            const intensity = value / maxValue;
                            
                            return (
                              <div
                                key={monthIdx}
                                className="aspect-square rounded flex items-center justify-center text-xs"
                                style={{
                                  backgroundColor: `rgba(255, 56, 92, ${Math.max(intensity, 0.1)})`,
                                  color: intensity > 0.5 ? 'white' : '#666'
                                }}
                                title={`${month} ${day}: ${value} nights`}
                              >
                                {value > 0 ? value : ''}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  
                  {/* Legend */}
                  <div className="flex items-center justify-end gap-2 mt-4">
                    <span className="text-xs text-gray-500">Less</span>
                    <div className="flex gap-1">
                      {[0.1, 0.3, 0.5, 0.7, 0.9].map((intensity, idx) => (
                        <div
                          key={idx}
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: `rgba(255, 56, 92, ${intensity})` }}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">More</span>
                  </div>
                </CardContent>
              </Card>
              
              {/* Tips */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Sun className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-900">High Demand Periods</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Dark cells indicate high booking activity. Consider raising prices during these times.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Moon className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-900">Low Demand Periods</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Light cells show slow periods. Offer discounts or promotions to boost bookings.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div className="text-center py-20 text-gray-500">
              Loading patterns...
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DemandForecastPage;
